import { TableInfo } from "api";
import { Form, Formik, useFormikContext, useField, FormikProps } from "formik";
import {
  DateTimeRangeField,
  Filter,
  FilterMetadata,
  Filters,
  SingleChoiceField,
} from "components/helpers/filterBuilder/FilterBuilder";
import { ColumnDetail } from "@avamae/table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SelectOption, Dropdown } from "components/dropdown/Dropdown";
import { toCamelCase } from "helpers/formatFormFieldNames";
import { connectableObservableDescriptor } from "rxjs/internal/observable/ConnectableObservable";
import { DEFAULT_B_ACTIVE_FILTER } from "components/Category/Category";

export const compareNewAndExistingFilters = (
  filters: Filter[],
  existingFilters: Filters | undefined
) => {
  let existingFiltersMatchNewFilters = true;

  const parsedExistingFilters = existingFilters
    ?.map(item => {
      if (
        item?.filters &&
        Array.isArray(item.filters) &&
        item.filters.length > 0
      ) {
        return item.filters[0];
      } else {
        return null;
      }
    })
    .filter(item => item !== null);

  if ((filters && !existingFilters) || (!filters && existingFilters) || (existingFilters && filters.length != existingFilters.length)) {
    existingFiltersMatchNewFilters = false;
  } else {
    filters.forEach(item => {
      if (parsedExistingFilters) {
        const match = parsedExistingFilters.filter(item2 => {
          const columnKeyMatches = item2?.columnKey === item?.columnKey;
          const operatorMatches = item2?.operator === item?.operator;
          const valueMatches = item2?.value === item?.value;

          return columnKeyMatches && operatorMatches && valueMatches;
        });

        if (match.length === 0) {
          existingFiltersMatchNewFilters = false;
        }
      }
    });
  }

  return existingFiltersMatchNewFilters;
};

export const HeaderDocuComp = ({
  selectedColumns,
  defaultBActiveFilterColumnKey,
}: {
  selectedColumns?: string[];
  defaultBActiveFilterColumnKey?: string;
}) => ({ table, onDone }: { table: TableInfo; onDone(): void }) => {
  return (
    <div className="DocuHeaderFilter">
      <HeaderFilter
        {...{
          table,
          onDone,
          selectedFilterFields: selectedColumns,
          defaultBActiveFilterColumnKey,
        }}
      />
    </div>
  );
};

const updateFormikFilterValues = (
  formikRef: React.RefObject<any>,
  table: TableInfo
) => {
  if (
    table &&
    table.data &&
    table.data.details &&
    table.data.details.filters &&
    formikRef.current &&
    formikRef.current.values
  ) {
    const currentFilterValues = table.data.details.filters;
    const currentFormikValues = formikRef.current.values;

    if (
      Array.isArray(currentFilterValues) &&
      currentFilterValues.length > 0 &&
      typeof currentFormikValues === "object"
    ) {
      currentFilterValues.forEach(item => {
        const columnKey = item.columnKey;

        if (
          currentFormikValues.hasOwnProperty(columnKey) &&
          currentFormikValues[columnKey as keyof typeof currentFormikValues] !==
            item
        ) {
          if (formikRef.current.setFieldValue) {
            formikRef.current.setFieldValue(columnKey, item);
          }
        }
      });
    }
  }
};

const HeaderFilter = ({
  table,
  onDone,
  selectedFilterFields,
  defaultBActiveFilterColumnKey,
}: {
  table: TableInfo;
  onDone(): void;
  selectedFilterFields?: string[];
  defaultBActiveFilterColumnKey?: string;
}) => {
  const columns: ColumnDetail[] =
    table && table.data ? table.data.details.columns : [];
  const [keyVal, setKeyVal] = React.useState(0);

  const [metadata, initialValues] = React.useMemo(() => {
    let meta: Record<string, FilterMetadata> = {};
    let values: Record<string, { columnKey: string; filters: Filter[] }> = {};
    table.data?.details.columns.forEach(entry => {
      if (entry.bFilterable) meta[entry.columnKey] = entry.filterMetadata;
      if (entry.bFilterable)
        values[entry.columnKey] = table.data?.details.filters.find(
          f => f.columnKey === entry.columnKey
        ) ?? { columnKey: entry.columnKey, filters: [] };
    });

    return [meta, values];
  }, [table]);

  if (columns) {
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={() => {}}
        enableReinitialize
      >
        {formik => (
          <HeaderFilterForm
            {...{
              columns,
              initialValues,
              metadata,
              selectedFilterFields: selectedFilterFields ?? [],
              setFilters: table.data?.actions.setFilters,
              rawListData: table?.data?.details?.listData,
              defaultBActiveFilterColumnKey: defaultBActiveFilterColumnKey,
              existingFilters: table?.data?.details?.filters,
            }}
          />
        )}
      </Formik>
    );
  }

  return <div className="Filters"></div>;
};

const HeaderFilterForm = ({
  columns,
  initialValues,
  metadata,
  selectedFilterFields,
  setFilters,
  rawListData,
  defaultBActiveFilterColumnKey,
  existingFilters,
}: {
  columns: ColumnDetail[];
  initialValues: Record<
    string,
    {
      columnKey: string;
      filters: Filter[];
    }
  >;
  metadata: Record<string, FilterMetadata>;
  selectedFilterFields: string[];
  setFilters?: (newFilters: Filter[]) => void;
  rawListData?: any[];
  defaultBActiveFilterColumnKey?: string;

  existingFilters?: Filters | undefined;
}) => {
  const { values } = useFormikContext<
    Record<string, { columnKey: string; filters: Filter[] }>
  >();
  const filterArray = useMemo(() => {
    return selectedFilterFields?.map(sf =>
      values[sf]?.filters.length === 0
        ? undefined
        : values[sf]?.filters[0]?.value
    );
  }, [values]);
  useEffect(() => {
    if (selectedFilterFields.every(sf => values[sf]?.filters.length === 0)) {
      const delayDebounceFn = setTimeout(() => {
        let newFilters = Object.values(values).flatMap(item => item.filters);

        const existingFiltersMatchNewFilters = compareNewAndExistingFilters(
          newFilters,
          existingFilters
        );

        if (!existingFiltersMatchNewFilters) {
          if (
            defaultBActiveFilterColumnKey &&
            values &&
            values.hasOwnProperty(defaultBActiveFilterColumnKey)
          ) {
            let newFilter = DEFAULT_B_ACTIVE_FILTER;
            newFilter.columnKey = defaultBActiveFilterColumnKey;

            const defaultFilterMatch = newFilters.filter(
              item => item.columnKey === defaultBActiveFilterColumnKey
            );

            if (defaultFilterMatch.length === 0) {
              newFilters = [...newFilters, newFilter];
            }

            setFilters && setFilters([...newFilters]);
          } else {
            setFilters && setFilters([...newFilters]);
          }
        }
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }

    const filters: Filter[] = Object.values(values).flatMap(
      filt => filt.filters
    );

    const existingFiltersMatchNewFilters = compareNewAndExistingFilters(
      filters,
      existingFilters
    );

    if (!existingFiltersMatchNewFilters) {
      const delayDebounceFn = setTimeout(() => {
        setFilters && setFilters(filters);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterArray]);

  const selectedFields: string[] = selectedFilterFields
    ? selectedFilterFields.map(c => c.toLowerCase())
    : [];
  return (
    <Form>
      <div className="Content">
        <div className="Form">
          <div className="Filters">
            {columns
              .filter(
                x =>
                  //x.bFilterable &&
                  !!selectedFields.includes(
                    x.columnKey.toString().toLowerCase()
                  )
              )
              .map(x => {
                switch (x.filterMetadata.filterType) {
                  case "TextContains":
                  case "SingleChoice": {
                    const placeholder =
                      x.columnKey === "DocumentLevels__DocumentLevelsName" ||
                      x.labelKey === "DocumentLevels"
                        ? "Level"
                        : "Type";
                    const selectLabel =
                      x.columnKey === "Documents__DocumentLevel" ||
                      x.labelKey === "DocumentLevel"
                        ? "Select Level..."
                        : "Select Type...";
                    let options: any[] = [];
                    if (x.filterMetadata.filterType && rawListData) {
                      options = [
                        ...new Set(
                          rawListData?.map(
                            d => d[toCamelCase(x.columnKey.toString())]
                          )
                        ),
                      ];
                    }
                    return (
                      <div key={x.columnKey.toString()}>
                        <SingleChoiceField
                          name={x.columnKey.toString()}
                          label={x.labelValue}
                          keyVal={0}
                          isClearable={true}
                          possibleValues={
                            x.filterMetadata.details?.PossibleValues
                              ? [...x.filterMetadata.details.PossibleValues]
                              : [
                                  {
                                    label: placeholder,
                                    value: undefined,
                                  },
                                  ...(options?.map(o => ({
                                    label: o,
                                    value: o,
                                  })) ?? []),
                                ]
                          }
                          placeholder={placeholder}
                        />
                      </div>
                    );
                  }
                  case "DateTime:Range":
                    return (
                      <div key={x.columnKey.toString()}>
                        <DateTimeRangeField
                          name={x.columnKey.toString()}
                          label={x.labelValue}
                          disableTyping={true}
                          isClearable={true}
                        />
                      </div>
                    );

                  default:
                    return <></>;
                }
              })}
          </div>
        </div>
      </div>
    </Form>
  );
};

const textOperators = [
  { value: "CONTAINS", label: "Contains" },
  { value: "EQ", label: "Equals" },
];
const TextContainsField: React.FC<{ name: string; label: string }> = ({
  name,
  label,
}) => {
  const [operator, setOperator] = useState(textOperators[1]);
  const [field, , helpers] = useField(name);

  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value ?? "";

  function handleChange(e: SelectOption) {
    const newValue = e.value;
    const filters: Filter[] = [];

    if (newValue.length > 0) {
      filters.push({
        columnKey: field.value.columnKey,
        operator: operator.value,
        value: newValue,
      });
    }
    helpers.setValue({
      columnKey: field.value.columnKey,
      filters,
    });
  }

  return (
    <div className="FormBox FormSideBySide">
      {/* <div className="FormLabel">
        <label>{label}</label>
      </div> */}
      <div className="InputContainers">
        <div className="FormField">
          <Dropdown
            key={`${name}`}
            //className="EditBox"
            value={value}
            options={[]}
            onChange={handleChange}
            roundedRectangle
          />
        </div>
      </div>
    </div>
  );
};
